import React, { useState, useEffect, useMemo } from "react";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { shippingLabel } from "../constants/shippingPlans";

const ProductList = () => {
  const [productList, setProductList] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}/products`);
      const data = await res.json();

      if (data.success) {
        setProductList(data.data.products || []);
      } else {
        alert("Failed to fetch products");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching products");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(
        `${config.BASE_URL}/products/${deleteDialog.id}`,
        { method: "DELETE" }
      );
      const data = await res.json();

      if (data.success) {
        setProductList((prev) =>
          prev.filter((p) => p.id !== deleteDialog.id)
        );
        setDeleteDialog({ open: false, id: null });
      } else {
        alert("Failed to delete product");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting product");
    }
  };

  const sortedProducts = useMemo(() => {
    let sortable = [...productList];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aVal = a[sortConfig.key]?.toString().toLowerCase() || "";
        const bVal = b[sortConfig.key]?.toString().toLowerCase() || "";
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [productList, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const totalEntries = sortedProducts.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return sortedProducts.slice(start, start + entriesPerPage);
  }, [sortedProducts, currentPage, entriesPerPage]);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  return (
    <div className="p-6 bg-[#211436] min-h-screen font-sans text-white">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg
              className="animate-spin h-6 w-6 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              ></path>
            </svg>
            <span className="text-gray-800 font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-4 flex flex-wrap justify-end items-center gap-3 bg-[#351F57] rounded-[6px] px-4 py-3">
        <button
          type="button"
          onClick={() => navigate("/dashboard/add-product")}
          className="bg-white text-[#5832AD] px-4 py-2 rounded hover:bg-[#111111] transition w-full sm:w-auto"
        >
          Add New Product +
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto p-3 bg-[#351F57] rounded-[6px] border border-transparent text-black">
        <table className="w-full text-left text-sm text-white table-fixed border border-gray-200">
          <thead className="bg-[#482772] border-b border-gray-300">
            <tr className="divide-x divide-gray-300">
              <th className="p-4 cursor-pointer" onClick={() => requestSort("title")}>
                Title {getSortIcon("title")}
              </th>
              <th className="p-4 cursor-pointer text-center" onClick={() => requestSort("price")}>
                Price {getSortIcon("price")}
              </th>
              <th className="p-4 text-center">Shipping</th>
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {paginatedProducts.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-4 text-center text-gray-400">
                  No products found.
                </td>
              </tr>
            ) : (
              paginatedProducts.map((product, index) => (
                <tr
                  key={product.id}
                  className={`${
                    index % 2 === 0 ? "bg-[#482772]" : "bg-[#351F57]"
                  } divide-x divide-gray-300`}
                >
                  {/* Title with image */}
                  <td className="p-4 font-semibold text-white flex items-center space-x-2">
                    <div className="w-[50px] h-[50px] flex-shrink-0 flex items-center justify-center">
                      <img
                        src={`${config.BASE_URL}/uploads/${product.url}`}
                        alt={product.title}
                        className="w-[50px] h-[50px] object-cover rounded-[8px] bg-gray-200"
                      />
                    </div>
                    <span className="truncate max-w-[200px]">{product.title}</span>
                  </td>

                  {/* Price */}
                  <td className="p-4 text-center font-medium">${product.price}</td>

                  <td className="p-4 text-center text-xs">
                    {shippingLabel(product.shippingType)}
                  </td>

                  {/* Action */}
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/edit-product/${product.id}`)}
                        className="px-3 py-1 text-sm bg-[#5832AD] text-white rounded-md hover:bg-purple-700"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteDialog({ open: true, id: product.id })}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between text-white text-sm space-y-2 md:space-y-0">
          <div className="font-bold">
            Showing{" "}
            {totalEntries === 0
              ? 0
              : (currentPage - 1) * entriesPerPage + 1}{" "}
            to {Math.min(currentPage * entriesPerPage, totalEntries)} of{" "}
            {totalEntries} entries
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 rounded-full hover:bg-[#351F57] hover:text-white disabled:opacity-50"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`px-3 py-1 rounded ${
                  page === currentPage
                    ? "bg-[#351F57] text-white"
                    : "hover:bg-gray-200"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded-full hover:bg-[#351F57] hover:text-white disabled:opacity-50"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Delete Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800">Delete Product</h2>
            <p className="text-gray-600 mt-2">
              Are you sure you want to delete this product?
            </p>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteDialog({ open: false, id: null })}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
